import Salutation from '@/types/taxcard/Salutation';
import { Moment } from 'moment';
import { ValidationError, Validator } from 'class-validator';
import i18next from 'i18next';

export default class User {
  public email: string;
  public password: string;
  public salutation: Salutation | string;
  public title: string;
  public firstName: string;
  public lastName: string;
  public birthday: Moment | null;
  public addressStreetAndNumber: string;
  public addressZipCode: string;
  public addressCity: string;
  public cellphoneNumber: string;
  public acceptedTermsAndConditions: boolean;
  public acceptedPrivacyTerms: boolean;

  public errors: ValidationError[] = [];

  public constructor(
    email?: string,
    password?: string,
    salutation?: Salutation,
    title?: string,
    firstName?: string,
    lastName?: string,
    birthday?: Moment,
    addressStreetAndNumber?: string,
    addressZipCode?: string,
    addressCity?: string,
    cellphoneNumber?: string,
    acceptedTermsAndConditions?: boolean,
    acceptedPrivacyTerms?: boolean
  ) {
    this.email = email || '';
    this.password = password || '';
    this.salutation = salutation || '';
    this.title = title || '';
    this.firstName = firstName || '';
    this.lastName = lastName || '';
    this.birthday = birthday || null;
    this.addressStreetAndNumber = addressStreetAndNumber || '';
    this.addressZipCode = addressZipCode || '';
    this.addressCity = addressCity || '';
    this.cellphoneNumber = cellphoneNumber || '';
    this.acceptedTermsAndConditions = acceptedTermsAndConditions || false;
    this.acceptedPrivacyTerms = acceptedPrivacyTerms || false;
  }

  public validateStepOne(resetErrors: boolean = true, validator: Validator = new Validator()): boolean {
    if (resetErrors) {
      this.errors = [];
    }

    const t = i18next.t.bind(i18next);

    if (validator.isEmpty(this.salutation)) {
      this.addError('salutation', 'isEmpty', t('user.validation.salutation.isEmpty'));
    }

    if (validator.isEmpty(this.firstName)) {
      this.addError('firstName', 'isEmpty', t('user.validation.firstName.isEmpty'));
    }

    if (validator.isEmpty(this.lastName)) {
      this.addError('lastName', 'isEmpty', t('user.validation.lastName.isEmpty'));
    }

    if (this.birthday === null) {
      this.addError('birthday', 'isEmpty', t('user.validation.birthday.isEmpty'));
    } else if (!this.birthday.isValid() || this.birthday.year() === 0) {
      this.addError('birthday', 'malformed', t('user.validation.birthday.malformed'));
    }

    return !this.hasErrors();
  }

  public validateStepTwo(resetErrors: boolean = true, validator: Validator = new Validator()): boolean {
    if (resetErrors) {
      this.errors = [];
    }

    const t = i18next.t.bind(i18next);

    if (validator.isEmpty(this.email)) {
      this.addError('email', 'isEmpty', t('user.validation.email.isEmpty'));
    }

    if (!/^[a-zA-Z0-9!#$%&'*+-/=?^_`{|}~.]+@/.test(this.email)) {
      this.addError('email', 'isNotEmail', t('users.validation.email.invalidCharacter'));
    }

    if (!validator.isEmail(this.email)) {
      this.addError('email', 'isEmail', t('user.validation.email.isEmail'));
    }

    if (validator.isEmpty(this.password)) {
      this.addError('password', 'isEmpty', t('user.validation.password.isEmpty'));
    }

    if (this.cellphoneNumber) {
      if (!validator.isNumberString(this.cellphoneNumber)) {
        this.addError('cellphoneNumber', 'isNumberString', t('user.validation.cellphoneNumber.isNumberString'));
      } else if (this.cellphoneNumber[0] !== '0' && this.cellphoneNumber[0] !== '+') {
        this.addError(
          'cellphoneNumber',
          'notStartingWithZeroOrPlus',
          t('user.validation.cellphoneNumber.notStartingWithZeroOrPlus')
        );
      }
    }

    if (!this.acceptedTermsAndConditions) {
      this.addError('acceptedTermsAndConditions', 'accepted', t('user.validation.acceptedTermsAndConditions.accepted'));
    }

    if (!this.acceptedPrivacyTerms) {
      this.addError('acceptedPrivacyTerms', 'accepted', t('user.validation.acceptedPrivacyTerms.accepted'));
    }

    return !this.hasErrors();
  }

  public validate(): boolean {
    this.errors = [];
    const validator = new Validator();

    this.validateStepOne(false, validator);
    this.validateStepTwo(false, validator);

    return !this.hasErrors();
  }

  public addError(property: string, constraintType: string, message: string): void {
    const errorExisting = this.errors.find(err => err.property === property);
    if (errorExisting) {
      errorExisting.constraints[constraintType] = message;
    } else {
      this.errors.push({
        property,
        constraints: { [constraintType]: message },
        children: [],
      });
    }
  }

  public hasErrors(): boolean {
    return this.errors.length > 0;
  }
}
